import Vue from 'vue';
import type User from '@/models/user/User';
import AuthService from '@/modules/user/AuthService';
import EnvironmentService from '@/env/EnvironmentService';
import { useModalStore } from '@/components/L/Modal/useModalStore';
import { NotificationPresenter } from '@/modules/notifications/services/NotificationPresenter';
import { NotificationStorageService } from '@/modules/notifications/services/NotificationStorageService';
import { NOTIFICATIONS } from '@/modules/notifications/registry/notificationsRegistry';
import {
  NotificationDescriptor,
  NotificationScope
} from '@/modules/notifications/models/NotificationDescriptor';

export interface RunEligibleOptions {
  scope: NotificationScope;
  /** Required when `scope === NotificationScope.ROUTE`; the `name` of the route just entered. */
  routeName?: string;
}

/**
 * Shows contextual "fixes & news" notifications using the Lenda UI (centered modal, or a
 * tutorial-style bubble when a notification targets an on-screen element), completely decoupled
 * from the tutorial lifecycle: it never touches `Tour.start()`, `canStartOnboarding`, the tour
 * enable-switch, or the `'onboarding'` storage key (AC1).
 *
 * Two trigger sites feed it:
 *   - GLOBAL — NavigationWrapper.mounted() after the BIK block (first login / shell mount).
 *   - ROUTE  — the router afterEach guard, keyed on the entered route name (first tab entry).
 *
 * A single promise mutex serialises every call so the two triggers can never overlap.
 */
export class PlatformNotificationService {
  /** Serialises all runEligible calls so GLOBAL and ROUTE evaluations never interleave. */
  private queue: Promise<void> = Promise.resolve();
  private readonly storage = new NotificationStorageService();
  private readonly presenter = new NotificationPresenter();

  public runEligible(options: RunEligibleOptions): Promise<void> {
    const run = this.queue.then(() => this.runEligibleInternal(options));
    // The serialization chain must never reject (a failed run would wedge the mutex), but the
    // returned promise should surface real errors rather than swallow them.
    this.queue = run.catch(() => undefined);
    return run;
  }

  private isMobile(): boolean {
    // Suppress only on real mobile (phones / small tablets, < 960px). Smaller laptops and
    // split-screen (the `md` range, ≥ 960px) still get notifications.
    return Boolean(Vue.prototype.$vuetify?.breakpoint?.smAndDown);
  }

  private isAnyModalOpen(): boolean {
    return useModalStore().modals.length > 0;
  }

  private async runEligibleInternal(options: RunEligibleOptions): Promise<void> {
    const user: Nullable<User> = AuthService.User;
    // Suppress on mobile (per product), and when there is no authenticated CRM user.
    if (!user || this.isMobile()) {
      return;
    }

    // Cheap, synchronous filtering BEFORE any UiStorage read — the common zero-eligible
    // login/navigation then adds no network call.
    const candidates: NotificationDescriptor[] = NOTIFICATIONS
      .filter((notification) => this.matchesContext(notification, options, user))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

    if (!candidates.length) {
      return;
    }

    for (const notification of candidates) {
      // Don't stack on top of an open modal (e.g. an in-progress tour modal); the remaining
      // notifications surface on the next trigger.
      if (this.isAnyModalOpen()) {
        return;
      }
      if (await this.storage.isSeen(notification.id)) {
        continue;
      }
      await this.show(notification);
    }
  }

  private matchesContext(notification: NotificationDescriptor, options: RunEligibleOptions, user: User): boolean {
    if (notification.scope !== options.scope) {
      return false;
    }
    if (notification.scope === NotificationScope.ROUTE &&
      (!options.routeName || !notification.routeNames?.includes(options.routeName))) {
      return false;
    }
    if (!this.matchesTenant(notification)) {
      return false;
    }
    if (notification.audience && !notification.audience(user)) {
      return false;
    }
    return true;
  }

  private matchesTenant(notification: NotificationDescriptor): boolean {
    if (!notification.tenants?.length) {
      return true; // omitted => both tenants
    }
    const activeDomain = EnvironmentService.Environment.isAppDomain();
    return notification.tenants.some((tenant) => activeDomain[tenant]);
  }

  private async show(notification: NotificationDescriptor): Promise<void> {
    // Mark seen only AFTER the user actually dismisses it (button / ✕), not on display: a page
    // refresh drops this promise, so an un-acknowledged notification re-shows next time rather than
    // being silently consumed. Mirrors how a tour persists until completed/cancelled.
    await this.presenter.present(notification);
    await this.storage.markSeen(notification.id);
  }
}
